import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  message,
  Alert,
  Tooltip,
  Typography,
  Modal,
  Progress,
  List,
  Badge,
  Select,
  Input,
  Spin,
} from "antd";
import {
  ShoppingCartOutlined,
  LinkOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  CloudUploadOutlined,
  EditOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Booking } from "../../types/booking";
import { PicqerService } from "../../services/picqerService";
import { WmsPurchaseOrderService } from "../../services/wmsPurchaseOrderService";
import { WmsProductLinkService } from "../../services/wmsProductLinkService";
import { formatDate } from "../../utils/bookingUtils";
import { supabase } from "../../lib/supabase";

const { Text } = Typography;

/** Normalize EAN for comparison (trim leading/trailing zeros) so e.g. 1234567890 and 123456789000 match. */
function normalizeEan(v: string | number | null | undefined): string | undefined {
  if (v == null || v === "") return undefined;
  const s = String(v).trim();
  if (!s) return undefined;
  return s.replace(/^0+/, "").replace(/0+$/, "") || s;
}

interface PurchaseOrderSectionProps {
  booking: Booking;
  onRefresh: () => void;
}

interface PurchaseOrder {
  id: number;
  picqer_reference: string | null;
  booking_reference: string | null;
  picqer_purchase_order_id: number | null;
  picqer_purchaseorderid?: string | null;
  status: string;
  supplier_name?: string | null;
  created_at: string;
  channeldock_delivery_id?: number | null;
  channeldock_reference?: string | null;
}

interface ProductAnalysis {
  id: number;
  name: string;
  ean: string | null;
  pieces: number | null;
  cartons: number | null;
  hasEAN: boolean;
  hasCompanyProduct: boolean;
  hasPicqerProduct: boolean;
  picqerProductId: number | null;
  companyProductId: number | null;
  status: "ready" | "needs_picqer_link" | "needs_company_product" | "needs_ean";
  canInclude: boolean;
  hasChannelDockProduct?: boolean;
  channeldockProductId?: number | null;
}

interface ProductAnalysisResponse {
  total: number;
  ready: number;
  needingSetup: number;
  products: ProductAnalysis[];
}

interface ProductCreationResult {
  productId: number;
  success: boolean;
  picqerProductId?: number;
  companyProductId?: number;
  error?: string;
}

const PurchaseOrderSection: React.FC<PurchaseOrderSectionProps> = ({
  booking,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [picqerIntegrationValid, setPicqerIntegrationValid] = useState(false);
  const [integrationError, setIntegrationError] = useState<string | null>(null);
  const [paymentArrears, setPaymentArrears] = useState(false);
  const [productAnalysis, setProductAnalysis] =
    useState<ProductAnalysisResponse | null>(null);
  const [loadingProductAnalysis, setLoadingProductAnalysis] = useState(false);
  const [deletingPurchaseOrder, setDeletingPurchaseOrder] = useState<
    number | null
  >(null);
  const [creatingProducts, setCreatingProducts] = useState(false);
  const [productCreationResults, setProductCreationResults] = useState<
    ProductCreationResult[]
  >([]);
  const [purchaseOrderError, setPurchaseOrderError] = useState<string | null>(null);
  const [showCreateProductsModal, setShowCreateProductsModal] = useState(false);
  const [creatingProductIds, setCreatingProductIds] = useState<Set<number>>(
    new Set()
  );
  const [relinkModalVisible, setRelinkModalVisible] = useState(false);
  const [relinkingProduct, setRelinkingProduct] = useState<ProductAnalysis | null>(null);
  const [picqerProducts, setPicqerProducts] = useState<any[]>([]);
  const [loadingPicqerProducts, setLoadingPicqerProducts] = useState(false);
  const [picqerProductSearch, setPicqerProductSearch] = useState("");
  const [relinking, setRelinking] = useState(false);
  const [unlinkingAllFromWms, setUnlinkingAllFromWms] = useState(false);

  const isStorageBooking =
    !booking.booking_delivery_info ||
    (Array.isArray(booking.booking_delivery_info) &&
      booking.booking_delivery_info.length === 0);

  useEffect(() => {
    loadPurchaseOrders();
    checkPicqerIntegration();

    // For storage bookings, always fetch product analysis
    if (isStorageBooking) {
      loadProductAnalysis();
    }
  }, [booking.id, isStorageBooking]);

  const [wmsProvider, setWmsProvider] = useState<"picqer" | "channeldock">("picqer");

  const checkPicqerIntegration = async () => {
    try {
      const { data: companyWms, error } = await supabase
        .from("company")
        .select(
          "wms_provider, picqer_enabled, picqer_api_key, picqer_fulfilment_customer_id, channeldock_enabled, channeldock_api_key, channeldock_api_secret, payment_arrears"
        )
        .eq("id", booking.company_id)
        .single();

      if (error || !companyWms) {
        setPicqerIntegrationValid(false);
        setIntegrationError("Unable to check WMS integration");
        return;
      }

      setPaymentArrears(companyWms.payment_arrears === true);

      if (companyWms.wms_provider === "channeldock") {
        setWmsProvider("channeldock");
        const valid =
          !!companyWms.channeldock_enabled &&
          !!companyWms.channeldock_api_key &&
          !!companyWms.channeldock_api_secret;
        setPicqerIntegrationValid(valid);
        setIntegrationError(valid ? null : "Company not connected to ChannelDock");
        return;
      }

      setWmsProvider("picqer");
      if (!companyWms.picqer_enabled || !companyWms.picqer_api_key) {
        setPicqerIntegrationValid(false);
        setIntegrationError("Company not connected to Picqer");
        return;
      }
      if (!companyWms.picqer_fulfilment_customer_id) {
        setPicqerIntegrationValid(false);
        setIntegrationError("No fulfillment customer configured");
        return;
      }
      setPicqerIntegrationValid(true);
      setIntegrationError(null);
    } catch (err) {
      console.error("Error checking WMS integration:", err);
      setPicqerIntegrationValid(false);
      setIntegrationError("Error checking integration");
    }
  };

  const loadPurchaseOrders = async () => {
    setLoading(true);
    try {
      const result = await PicqerService.getPurchaseOrdersForBooking(
        booking.id
      );
      if (result.success && result.data) {
        setPurchaseOrders(result.data);
      } else {
        console.error("Failed to load purchase orders:", result.error);
        setPurchaseOrders([]);
      }
    } catch (error) {
      console.error("Error loading purchase orders:", error);
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProductAnalysis = async () => {
    setLoadingProductAnalysis(true);
    try {
      const { data: companyWms } = await supabase
        .from("company")
        .select("wms_provider")
        .eq("id", booking.company_id)
        .single();
      const provider = companyWms?.wms_provider === "channeldock" ? "channeldock" : "picqer";
      const result = await WmsPurchaseOrderService.analyzeProductsForBooking(booking.id, provider);
      if (result.success && result.data) {
        setProductAnalysis(result.data as ProductAnalysisResponse);
      } else {
        console.error("Failed to load product analysis:", result.error);
        message.error("Failed to load product analysis");
      }
    } catch (error) {
      console.error("Error loading product analysis:", error);
      message.error("Error loading product analysis");
    } finally {
      setLoadingProductAnalysis(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadPurchaseOrders(), loadProductAnalysis()]);
  };

  const handleCreatePurchaseOrder = async () => {
    if (!isStorageBooking) {
      message.error("Purchase orders can only be created for storage bookings");
      return;
    }

    if (creating) {
      console.log(
        "Purchase order creation already in progress, ignoring duplicate request"
      );
      return;
    }

    setCreating(true);
    try {
      // First check if company has proper Picqer integration with fulfillment customer
      const { data: companyWms, error: companyError } = await supabase
        .from("company")
        .select(
          "wms_provider, picqer_enabled, picqer_api_key, picqer_fulfilment_customer_id, channeldock_enabled, channeldock_api_key, channeldock_api_secret, payment_arrears"
        )
        .eq("id", booking.company_id)
        .single();

      if (companyError || !companyWms) {
        message.error("Unable to check WMS integration. Please try again.");
        return;
      }

      if (companyWms.payment_arrears === true) {
        setPaymentArrears(true);
        message.error(
          "Purchase order geblokkeerd wegens betalingsachterstand. Werk de status bij onder Admin > Bedrijven."
        );
        return;
      }

      const provider = companyWms.wms_provider === "channeldock" ? "channeldock" : "picqer";
      if (provider === "channeldock") {
        if (!companyWms.channeldock_enabled || !companyWms.channeldock_api_key || !companyWms.channeldock_api_secret) {
          message.error("Company not connected to ChannelDock. Set up ChannelDock in the integration tab.");
          return;
        }
      } else {
        if (!companyWms.picqer_enabled || !companyWms.picqer_api_key) {
          message.error("Company not connected to Picqer. Please set up WMS integration in the integration tab.");
          return;
        }
        if (!companyWms.picqer_fulfilment_customer_id) {
          message.error("No Picqer fulfillment customer configured. Please configure in the integration tab.");
          return;
        }
      }

      setPurchaseOrderError(null);
      const result = await WmsPurchaseOrderService.createPurchaseOrderForBooking(booking.id, provider);

      console.log("Purchase order creation result:", result);
      console.log("Full result object:", JSON.stringify(result, null, 2));

      if (result.success && result.data) {
        message.success("Purchase order created successfully!");
        setPurchaseOrderError(null);
        await loadPurchaseOrders();
        await loadProductAnalysis();
        onRefresh();
      } else {
        // Store the full edge-function body for display (same as Network tab response)
        const errorMessage = result.error || "Unknown error occurred";
        setPurchaseOrderError(errorMessage);

        const toastSnippet =
          errorMessage.length > 220 ? `${errorMessage.slice(0, 220)}…` : errorMessage;

        if (result.productAnalysis) {
          // Show product analysis even if PO creation failed
          setProductAnalysis(result.productAnalysis);
          message.warning(
            `Purchase order creation failed: ${toastSnippet} Some products need setup — see full details below.`
          );
        } else {
          message.error(
            `Failed to create purchase order. ${toastSnippet}${
              errorMessage.length > 220 ? " Full response below." : ""
            }`
          );
        }
      }
    } catch (error) {
      console.error("Error creating purchase order:", error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : String(error);
      setPurchaseOrderError(`Exception: ${errorMessage}`);
      message.error("An error occurred while creating the purchase order");
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePurchaseOrder = async (purchaseOrderId: number) => {
    setDeletingPurchaseOrder(purchaseOrderId);
    try {
      const result = await PicqerService.deletePurchaseOrder(purchaseOrderId);
      if (result.success) {
        message.success(
          `Purchase order deleted successfully. ${result.deletedProductsCount} associated products removed.`
        );
        await loadPurchaseOrders();
        await loadProductAnalysis();
        onRefresh();
      } else {
        message.error(`Failed to delete purchase order: ${result.error}`);
      }
    } catch (error) {
      console.error("Error deleting purchase order:", error);
      message.error("An error occurred while deleting the purchase order");
    } finally {
      setDeletingPurchaseOrder(null);
    }
  };

  const handleCreateMissingProducts = async (productIds: number[]) => {
    // Set loading state for individual products
    setCreatingProductIds((prev) => new Set([...prev, ...productIds]));
    setProductCreationResults([]);

    try {
      console.log("Creating missing products for booking:", {
        bookingId: booking.id,
        bookingIdType: typeof booking.id,
        productIds,
        productIdsType: typeof productIds,
      });

      const result = await WmsProductLinkService.createMissingProductsForBooking(
        booking.id,
        productIds,
        wmsProvider
      );

      console.log("Create missing products result:", result);

      if (result.success && result.data) {
        setProductCreationResults(result.data.results);

        if (result.data.createdCount > 0) {
          message.success(
            `Successfully created ${result.data.createdCount} products in WMS!`
          );

          if (result.data.errorCount > 0) {
            message.warning(
              `${result.data.errorCount} products failed to create. Check the results below.`
            );
          }

          // Reload product analysis to reflect the new products
          await loadProductAnalysis();
          onRefresh();
        } else {
          message.error("No products were created successfully");
        }
      } else {
        message.error(`Failed to create products: ${result.error}`);
      }
    } catch (error) {
      console.error("Error creating missing products:", error);
      message.error("An error occurred while creating products");
    } finally {
      // Clear loading state for the processed products
      setCreatingProductIds((prev) => {
        const newSet = new Set(prev);
        productIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  };

  const handleCreateAllMissingProducts = async () => {
    if (!productAnalysis) return;

    const missingProductIds = productAnalysis.products
      .filter((p) => !p.canInclude)
      .map((p) => p.id);

    if (missingProductIds.length === 0) {
      message.info("No missing products to create");
      return;
    }

    setCreatingProducts(true);
    setShowCreateProductsModal(true);
    await handleCreateMissingProducts(missingProductIds);
    setCreatingProducts(false);
  };

  const handleLinkAllToExistingProducts = async () => {
    if (!productAnalysis) return;

    const productsNeedingCompanyProduct = productAnalysis.products
      .filter(
        (p) =>
          p.status === "needs_company_product" ||
          p.status === "needs_picqer_link"
      )
      .map((p) => p.id);

    if (productsNeedingCompanyProduct.length === 0) {
      message.info("No products to link to existing WMS products");
      return;
    }

    setCreatingProducts(true);
    setShowCreateProductsModal(true);

    try {
      const result = await WmsProductLinkService.searchAndLinkExistingProducts(
        booking.id,
        productsNeedingCompanyProduct,
        wmsProvider
      );

      if (result.success && result.data) {
        setProductCreationResults(result.data.results);

        if (result.data.createdCount > 0) {
          message.success(
            `Successfully linked ${result.data.createdCount} products to existing WMS products!`
          );

          if (result.data.errorCount > 0) {
            message.warning(
              `${result.data.errorCount} products failed to link. Check the results below.`
            );
          }

          // Reload product analysis to reflect the new products
          await loadProductAnalysis();
          onRefresh();
        } else {
          message.error("No products were linked successfully");
        }
      } else {
        message.error(`Failed to link products: ${result.error}`);
      }
    } catch (error) {
      console.error("Error linking to existing products:", error);
      message.error("An error occurred while linking products");
    } finally {
      setCreatingProducts(false);
    }
  };

  const handleCreateSingleProduct = async (productId: number) => {
    await handleCreateMissingProducts([productId]);
  };

  const handleOpenRelinkModal = async (product: ProductAnalysis) => {
    setRelinkingProduct(product);
    setRelinkModalVisible(true);
    setPicqerProductSearch(""); // show all products by default; user can search to filter
    setLoadingPicqerProducts(true);

    try {
      if (wmsProvider === "channeldock") {
        const { data, error } = await supabase.functions.invoke("channeldock-product-import", {
          body: { action: "import", companyId: booking.company_id },
        });
        if (error) throw error;
        const result = data as { success?: boolean; data?: { picqerProducts?: Array<{ idproduct: number; name?: string; ean?: string; productcode?: string }> } };
        if (result?.success && result.data?.picqerProducts) {
          setPicqerProducts(result.data.picqerProducts);
        } else {
          message.error("Failed to load ChannelDock products");
        }
      } else {
        const { data: companyData } = await supabase
          .from("company")
          .select("picqer_api_key")
          .eq("id", booking.company_id)
          .single();

        if (!companyData?.picqer_api_key) {
          message.error("Picqer API key not configured");
          setRelinkModalVisible(false);
          return;
        }

        const result = await PicqerService.importProducts(
          companyData.picqer_api_key,
          booking.company_id!
        );

        if (result.success && result.data) {
          type PicqerProductItem = { idproduct: number; name?: string; ean?: string; productcode?: string; barcode?: string };
          const allProducts: PicqerProductItem[] = [
            ...(result.data.newProducts || []),
            ...(result.data.matchedProducts?.map((m: { picqerProduct: PicqerProductItem }) => m.picqerProduct) || []),
            ...(result.data.picqerProducts || []),
          ];
          const uniqueProducts = Array.from(
            new Map(allProducts.map((p) => [p.idproduct, p])).values()
          );
          setPicqerProducts(uniqueProducts);
        } else {
          message.error("Failed to load WMS products");
        }
      }
    } catch (error) {
      console.error("Error loading WMS products:", error);
      message.error("Error loading WMS products");
    } finally {
      setLoadingPicqerProducts(false);
    }
  };

  const handleRelinkProduct = async (wmsProductId: number) => {
    if (!relinkingProduct) {
      message.error("Invalid product");
      return;
    }

    setRelinking(true);
    try {
      const hasCompanyProduct = !!relinkingProduct.companyProductId && !!booking.company_id;
      const result = hasCompanyProduct
        ? await WmsProductLinkService.linkProduct(
            booking.company_id!,
            wmsProductId,
            relinkingProduct.companyProductId!,
            wmsProvider
          )
        : await WmsProductLinkService.linkBookingProductToWms(
            relinkingProduct.id,
            wmsProductId,
            wmsProvider
          );

      if (result.success) {
        message.success(hasCompanyProduct ? "Product relinked successfully!" : "Product linked successfully!");
        setRelinkModalVisible(false);
        setRelinkingProduct(null);
        await loadProductAnalysis();
        onRefresh();
      } else {
        message.error(result.error || (hasCompanyProduct ? "Failed to relink product" : "Failed to link product"));
      }
    } catch (error) {
      console.error("Error linking/relinking product:", error);
      message.error("An error occurred while linking product");
    } finally {
      setRelinking(false);
    }
  };

  const handleUnlinkProduct = async (product: ProductAnalysis) => {
    if (!product.companyProductId || !booking.company_id) {
      message.error("Invalid product or company");
      return;
    }

    Modal.confirm({
      title: "Unlink Product",
      content: `Are you sure you want to unlink this product from WMS? The product will need to be relinked before it can be included in purchase orders.`,
      onOk: async () => {
        try {
          const result = await WmsProductLinkService.unlinkProduct(
            booking.company_id!,
            product.companyProductId!,
            wmsProvider
          );

          if (result.success) {
            message.success("Product unlinked successfully!");
            await loadProductAnalysis();
            onRefresh();
          } else {
            message.error(result.error || "Failed to unlink product");
          }
        } catch (error) {
          console.error("Error unlinking product:", error);
          message.error("An error occurred while unlinking product");
        }
      },
    });
  };

  const handleUnlinkAllFromWms = () => {
    const companyId = booking.company_id;
    if (!companyId) {
      message.error("Missing company for this booking");
      return;
    }

    const providerLabel = wmsProvider === "channeldock" ? "ChannelDock" : "Picqer";

    Modal.confirm({
      title: `Unlink all booking products from ${providerLabel}`,
      content: (
        <div>
          <p>
            This clears the {providerLabel} product link on every company catalog row that matches a
            line on this booking (by EAN), for this company only. It does not delete products in{" "}
            {providerLabel} or remove booking lines.
          </p>
          <p style={{ marginBottom: 0 }}>
            Purchase orders already created are unchanged. You will need to link again before
            creating a new purchase order for these SKUs.
          </p>
        </div>
      ),
      okText: "Unlink all",
      okButtonProps: { danger: true },
      onOk: async () => {
        setUnlinkingAllFromWms(true);
        try {
          const result = await WmsProductLinkService.unlinkAllBookingProductsFromWms(
            booking.id,
            companyId,
            wmsProvider
          );
          if (result.success) {
            if (result.unlinkedCount === 0) {
              message.info(`No ${providerLabel}-linked catalog products matched this booking’s EANs.`);
            } else {
              message.success(
                `Unlinked ${result.unlinkedCount} catalog product(s) from ${providerLabel}.`
              );
            }
            await loadProductAnalysis();
            onRefresh();
          } else {
            message.error(result.error || "Failed to unlink products");
          }
        } catch (e) {
          console.error("Error unlinking all from WMS:", e);
          message.error("An error occurred while unlinking products");
        } finally {
          setUnlinkingAllFromWms(false);
        }
      },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "concept":
        return "blue";
      case "expected":
        return "orange";
      case "purchased":
        return "green";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "concept":
        return "Draft";
      case "expected":
        return "Expected";
      case "purchased":
        return "Purchased";
      case "cancelled":
        return "Cancelled";
      default:
        return status;
    }
  };

  const getProductStatusColor = (status: string) => {
    switch (status) {
      case "ready":
        return "green";
      case "needs_picqer_link":
        return "orange";
      case "needs_company_product":
        return "blue";
      case "needs_ean":
        return "red";
      default:
        return "default";
    }
  };

  const getProductStatusLabel = (status: string) => {
    switch (status) {
      case "ready":
        return "Ready";
      case "needs_picqer_link":
        return "Needs Link";
      case "needs_company_product":
        return "Needs Product";
      case "needs_ean":
        return "Needs EAN";
      default:
        return status;
    }
  };

  const columns = [
    {
      title: "Reference",
      dataIndex: "booking_reference",
      key: "booking_reference",
      render: (reference: string, record: PurchaseOrder) => {
        const refDisplay = reference || record.picqer_reference || record.channeldock_reference || "N/A";
        const isChannelDock = record.channeldock_delivery_id != null;
        const openUrl = isChannelDock
          ? "https://channeldock.com"
          : record.picqer_purchase_order_id
            ? `https://e-freight-forwarding.picqer.com/purchaseorders/${record.picqer_purchase_order_id}`
            : null;
        return (
          <Space>
            <Text strong>{refDisplay}</Text>
            {openUrl && (
              <Tooltip title={isChannelDock ? "Open in ChannelDock" : "View in Picqer (opens in new tab)"}>
                <Button
                  type="link"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => window.open(openUrl, "_blank")}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (name: string) => name || "-",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => formatDate(date),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: PurchaseOrder) => (
        <Space>
          <Tooltip title="Delete purchase order">
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingPurchaseOrder === record.id}
              onClick={() => handleDeletePurchaseOrder(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const productColumns = [
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: ProductAnalysis) => (
        <div>
          <div>
            <Text strong>{name}</Text>
          </div>
          {record.ean && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                EAN: {record.ean}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      key: "quantity",
      render: (record: ProductAnalysis) => {
        const qty = Math.max(record.pieces || 0, record.cartons || 0, 1);
        return <Text>{qty}</Text>;
      },
    },
    {
      title: wmsProvider === "channeldock" ? "ChannelDock Product ID" : "Picqer Product ID",
      key: "wmsProductId",
      render: (record: ProductAnalysis) => {
        const channeldockId = record.channeldockProductId ?? null;
        const picqerId = record.picqerProductId ?? null;
        const hasChanneldock = channeldockId != null;
        const hasPicqer = picqerId != null;
        if (hasChanneldock || hasPicqer) {
          const productId = hasChanneldock ? channeldockId : picqerId;
          const productUrl = hasChanneldock
            ? `https://channeldock.com/portal/public/centermyproducts?getProduct=${channeldockId}`
            : `https://e-freight-forwarding.picqer.com/products/${picqerId}`;
          return (
            <Space>
              <a
                href={productUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {productId}
              </a>
              {record.companyProductId && (
                <>
                  <Tooltip title="Relink to different WMS product">
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleOpenRelinkModal(record)}
                    />
                  </Tooltip>
                  <Tooltip title="Unlink from WMS">
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DisconnectOutlined />}
                      onClick={() => handleUnlinkProduct(record)}
                    />
                  </Tooltip>
                </>
              )}
            </Space>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: ProductAnalysis) => (
        <Space>
          <Tag color={getProductStatusColor(status)}>
            {getProductStatusLabel(status)}
          </Tag>
          {!record.canInclude && (
            <Space>
              {(record.status === "needs_company_product" ||
                record.status === "needs_picqer_link") && (
                <Button
                  type="default"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => handleOpenRelinkModal(record)}
                >
                  Link to Existing
                </Button>
              )}
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                loading={creatingProductIds.has(record.id)}
                onClick={() => handleCreateSingleProduct(record.id)}
              >
                Create in WMS
              </Button>
            </Space>
          )}
        </Space>
      ),
    },
  ];

  const wmsName = wmsProvider === "channeldock" ? "ChannelDock" : "Picqer";

  return (
    <div>
      {/* Payment arrears: block PO creation */}
      {isStorageBooking && paymentArrears && (
        <Alert
          type="error"
          showIcon
          message="Purchase order geblokkeerd – Betalingsachterstand"
          description={
            <span>
              Dit bedrijf heeft betalingsachterstand. Er kunnen geen purchase
              orders worden aangemaakt totdat de status is bijgewerkt.{" "}
              {booking.company_id && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() =>
                    navigate(`/admin/companies?id=${booking.company_id}`)
                  }
                >
                  Status bedrijf bijwerken
                </Button>
              )}
            </span>
          }
          style={{ marginBottom: 16 }}
          closable={false}
        />
      )}

      {/* Info Alert for Storage Bookings */}
      {isStorageBooking ? (
        !picqerIntegrationValid && integrationError ? (
          <Alert
            message="WMS Integration Required"
            description={`Cannot create purchase orders: ${integrationError}. Please configure WMS integration (Picqer or ChannelDock) in the integration tab first.`}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : (
          !paymentArrears && (
            <Alert
              message="Storage Booking - Purchase Orders Available"
              description="This booking is for warehouse storage. Purchase orders can be created manually to manage inventory in your WMS (Picqer or ChannelDock)."
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
          )
        )
      ) : (
        <Alert
          message="Delivery Booking - No Purchase Orders Needed"
          description="This booking has a delivery address. Purchase orders are only created for storage bookings."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Show which WMS will be used for creating POs */}
      {isStorageBooking && picqerIntegrationValid && !paymentArrears && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Creating purchase orders in: <Text strong>{wmsName}</Text>
          </Text>
        </div>
      )}

      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            Purchase Orders
            {purchaseOrders.length > 0 && (
              <Tag color="blue">{purchaseOrders.length}</Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={refreshAll}
              loading={loading}
            >
              Refresh
            </Button>
            {isStorageBooking && picqerIntegrationValid && booking.company_id && (
              <Button
                danger
                icon={<DisconnectOutlined />}
                onClick={handleUnlinkAllFromWms}
                loading={unlinkingAllFromWms}
              >
                Unlink all from WMS
              </Button>
            )}
            {isStorageBooking && (
              <>
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  onClick={handleCreatePurchaseOrder}
                  loading={creating}
                  disabled={
                    creating ||
                    purchaseOrders.length > 0 ||
                    !picqerIntegrationValid ||
                    paymentArrears
                  }
                  title={
                    paymentArrears
                      ? "Purchase order geblokkeerd wegens betalingsachterstand"
                      : !picqerIntegrationValid && integrationError
                      ? `Cannot create purchase order: ${integrationError}. Please configure WMS integration in the integration tab.`
                      : purchaseOrders.length > 0
                      ? "Purchase order already exists"
                      : "Create purchase order for this storage booking"
                  }
                >
                  {purchaseOrders.length > 0
                    ? "Already Created"
                    : paymentArrears
                    ? "Blocked – Payment Arrears"
                    : !picqerIntegrationValid
                    ? "Integration Required"
                    : "Create Purchase Order"}
                </Button>
              </>
            )}
          </Space>
        }
      >
        {/* Display full error message if purchase order creation failed */}
        {purchaseOrderError && (
          <Alert
            message="Purchase Order Creation Failed"
            description={
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Error Details:
                </Text>
                <Text
                  code
                  style={{
                    display: "block",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: 12,
                    backgroundColor: "#fff",
                    padding: 8,
                    borderRadius: 4,
                    maxHeight: 480,
                    overflow: "auto",
                  }}
                >
                  {purchaseOrderError}
                </Text>
                <Button
                  type="link"
                  size="small"
                  onClick={() => setPurchaseOrderError(null)}
                  style={{ marginTop: 8, padding: 0 }}
                >
                  Dismiss
                </Button>
              </div>
            }
            type="error"
            showIcon
            closable
            onClose={() => setPurchaseOrderError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        {purchaseOrders.length > 0 ? (
          <>
            <Table
              columns={columns}
              dataSource={purchaseOrders}
              rowKey="id"
              pagination={false}
              size="small"
              loading={loading}
            />

            {/* Additional Info */}
            <div
              style={{
                marginTop: 16,
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Text type="secondary">
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                Purchase orders are created manually after storage bookings are
                approved. They help manage inventory and fulfillment in your WMS.
              </Text>
            </div>
          </>
        ) : (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#666" }}
          >
            <ShoppingCartOutlined
              style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
            />
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              No Purchase Orders
            </div>
            <div style={{ fontSize: 14 }}>
              {isStorageBooking
                ? "No purchase orders have been created for this storage booking yet."
                : "Purchase orders are only created for storage bookings."}
            </div>
          </div>
        )}

        {/* Product Analysis Section - Always show for storage bookings */}
        {isStorageBooking && (
          <div style={{ marginTop: 16 }}>
            {/* Debug info */}
            {(() => {
              console.log("Product analysis state:", {
                loadingProductAnalysis,
                productAnalysis: productAnalysis
                  ? {
                      total: productAnalysis.total,
                      ready: productAnalysis.ready,
                      needingSetup: productAnalysis.needingSetup,
                      productsCount: productAnalysis.products?.length,
                    }
                  : null,
              });
              return null;
            })()}
            {loadingProductAnalysis ? (
              <Alert
                message="Loading Product Analysis"
                description="Analyzing products for purchase order readiness..."
                type="info"
                showIcon
              />
            ) : productAnalysis ? (
              productAnalysis.total > 0 ? (
                <Alert
                  message="Product Analysis for Purchase Order"
                  description={
                    <div>
                      <div style={{ marginBottom: 12 }}>
                        <Text>
                          {productAnalysis.ready} of {productAnalysis.total}{" "}
                          products are ready for purchase orders.
                          {productAnalysis.needingSetup > 0 && (
                            <>
                              {" "}
                              <Text type="warning">
                                {productAnalysis.needingSetup} products need
                                setup.
                              </Text>
                            </>
                          )}
                        </Text>
                      </div>

                      {/* Action buttons for missing products */}
                      {productAnalysis.needingSetup > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <Space>
                            <Button
                              type="primary"
                              icon={<CloudUploadOutlined />}
                              onClick={handleCreateAllMissingProducts}
                              loading={creatingProducts}
                            >
                              Create All Missing Products
                            </Button>
                            <Button
                              type="default"
                              icon={<LinkOutlined />}
                              onClick={handleLinkAllToExistingProducts}
                              loading={creatingProducts}
                            >
                              Link All to Existing
                            </Button>
                            <Text type="secondary">
                              "Create" will create new products in your WMS.
                              "Link" will search for existing WMS products
                              and create company products for them.
                            </Text>
                          </Space>
                        </div>
                      )}

                      {/* Product Status Table */}
                      <Table
                        size="small"
                        dataSource={productAnalysis.products}
                        rowKey="id"
                        pagination={false}
                        columns={productColumns}
                      />
                    </div>
                  }
                  type="info"
                  showIcon
                />
              ) : (
                <Alert
                  message="No Products Found"
                  description="No products found for this booking."
                  type="warning"
                  showIcon
                />
              )
            ) : (
              <Alert
                message="Product Analysis Unavailable"
                description="Unable to load product analysis. Please try refreshing."
                type="warning"
                showIcon
              />
            )}
          </div>
        )}
      </Card>

      {/* Product Creation Results Modal */}
      <Modal
        title="Product Creation Results"
        open={showCreateProductsModal}
        onCancel={() => setShowCreateProductsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowCreateProductsModal(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {productCreationResults.length > 0 ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text>
                Created {productCreationResults.filter((r) => r.success).length}{" "}
                of {productCreationResults.length} products successfully.
              </Text>
            </div>

            <List
              size="small"
              dataSource={productCreationResults}
              renderItem={(result) => (
                <List.Item>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <div>
                      <Text strong>Product ID: {result.productId}</Text>
                      {result.success && (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {wmsName} product ID: {result.picqerProductId} | Company
                            product ID: {result.companyProductId}
                          </Text>
                        </div>
                      )}
                    </div>
                    <div>
                      {result.success ? (
                        <Badge status="success" text="Success" />
                      ) : (
                        <Badge status="error" text="Failed" />
                      )}
                    </div>
                  </div>
                  {!result.success && result.error && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="danger" style={{ fontSize: 12 }}>
                        Error: {result.error}
                      </Text>
                    </div>
                  )}
                </List.Item>
              )}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Text>No results to display</Text>
          </div>
        )}
      </Modal>

      {/* Relink Product Modal */}
      <Modal
        title={relinkingProduct?.companyProductId ? "Relink Product to WMS" : "Link to WMS Product"}
        open={relinkModalVisible}
        onCancel={() => {
          setRelinkModalVisible(false);
          setRelinkingProduct(null);
          setPicqerProductSearch("");
        }}
        footer={null}
        width={600}
      >
        {relinkingProduct && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Product: </Text>
              <Text>{relinkingProduct.name}</Text>
              {relinkingProduct.ean && (
                <>
                  <br />
                  <Text type="secondary">EAN: {relinkingProduct.ean}</Text>
                </>
              )}
              {(relinkingProduct.picqerProductId ?? relinkingProduct.channeldockProductId) != null && (
                <>
                  <br />
                  <Text type="secondary">
                    Currently linked to:{" "}
                    {wmsProvider === "channeldock" ? (
                      relinkingProduct.channeldockProductId ?? relinkingProduct.picqerProductId
                    ) : (
                      <a
                        href={`https://e-freight-forwarding.picqer.com/products/${relinkingProduct.picqerProductId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {relinkingProduct.picqerProductId}
                      </a>
                    )}
                  </Text>
                </>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Select New WMS Product:
              </Text>
              {loadingPicqerProducts ? (
                <Spin />
              ) : (
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  placeholder={
                    relinkingProduct?.ean
                      ? `Search for a WMS product (EAN: ${relinkingProduct.ean})...`
                      : "Search for a WMS product..."
                  }
                  optionFilterProp="children"
                  value={null}
                  onChange={(value: number) => {
                    if (value) {
                      handleRelinkProduct(value);
                    }
                  }}
                  loading={relinking}
                  filterOption={(input, option) => {
                    const optionText = option?.children?.toString() || "";
                    return optionText.toLowerCase().includes(input.toLowerCase());
                  }}
                  onSearch={setPicqerProductSearch}
                  defaultOpen={false}
                >
                  {(() => {
                    // Filter and sort products: exact EAN matches first, then others
                    const eanToMatch = relinkingProduct?.ean?.toString();
                    const filtered = picqerProducts.filter((p: any) => {
                      if (!picqerProductSearch) return true; // show all when search is empty
                      const search = picqerProductSearch.toLowerCase();
                      return (
                        p.name?.toLowerCase().includes(search) ||
                        p.productcode?.toLowerCase().includes(search) ||
                        p.ean?.toString().includes(search) ||
                        p.barcode?.toString().includes(search) ||
                        p.idproduct?.toString().includes(search)
                      );
                    });
                    
                    const normEan = normalizeEan(eanToMatch);
                    const sorted = filtered.sort((a: any, b: any) => {
                      if (!normEan) return 0;
                      const aNorm = normalizeEan(a.ean ?? a.barcode);
                      const bNorm = normalizeEan(b.ean ?? b.barcode);
                      const aEanMatch = aNorm === normEan;
                      const bEanMatch = bNorm === normEan;
                      if (aEanMatch && !bEanMatch) return -1;
                      if (!aEanMatch && bEanMatch) return 1;
                      return 0;
                    });

                    return sorted.map((product: any) => {
                      const pNorm = normalizeEan(product.ean ?? product.barcode);
                      const isEanMatch = !!normEan && pNorm === normEan;
                      
                      return (
                        <Select.Option
                          key={product.idproduct}
                          value={product.idproduct}
                          style={isEanMatch ? { backgroundColor: "#e6f7ff" } : undefined}
                        >
                          {isEanMatch && "⭐ "}
                          {product.name} {product.productcode ? `(${product.productcode})` : ""}{" "}
                          {product.ean ? `[EAN: ${product.ean}]` : ""} - ID: {product.idproduct}
                        </Select.Option>
                      );
                    });
                  })()}
                </Select>
              )}
              {relinkingProduct?.ean && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                  Products matching EAN {relinkingProduct.ean} are highlighted with ⭐
                </Text>
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <Button
                onClick={() => {
                  setRelinkModalVisible(false);
                  setRelinkingProduct(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PurchaseOrderSection;
